/**
 * A very small markdown renderer for support text.
 *
 * The subset is deliberate: paragraphs, **bold**, *italic*, `code`, fenced code
 * blocks, bulleted and numbered lists, block quotes, headings, and links. Enough to
 * paste a traceback, number the steps to reproduce, and point at a clause.
 *
 * What matters more than what it renders is *how*: the parser produces React
 * elements directly and never an HTML string. There is no `dangerouslySetInnerHTML`
 * anywhere on this path, so a `<script>` in a ticket is text that says "<script>",
 * whatever the author intended. This is the first place the product renders
 * user-authored prose with any formatting at all (CLAUDE.md 15.7), and keeping it
 * free of an HTML pipeline is what keeps that rule cheap.
 *
 * Links are limited to http and https. `javascript:` and `data:` URLs are not links
 * here; they are rendered as the literal text the author typed.
 */
import { Fragment, type ReactNode } from 'react'

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'link'; href: string; children: Inline[] }
  | { kind: 'break' }

export type Block =
  | { kind: 'paragraph'; children: Inline[] }
  | { kind: 'heading'; level: 1 | 2 | 3; children: Inline[] }
  | { kind: 'code'; text: string }
  | { kind: 'quote'; children: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] }

const SAFE_URL = /^https?:\/\/[^\s<>]+$/i

/** The trailing punctuation a bare URL almost never ends with. */
function trimAutolink(url: string): string {
  return url.replace(/[.,;:!?'")\]]+$/, '')
}

interface Built {
  node: Inline
  /** Characters consumed. Defaults to the whole match. */
  length?: number
}

interface InlineRule {
  pattern: RegExp
  build: (match: RegExpExecArray) => Built | null
}

const group = (match: RegExpExecArray, index: number): string => match[index] ?? ''

// Order matters only as a tiebreak when two rules match at the same index: code
// wins over emphasis so `**` inside a code span is left alone.
const INLINE_RULES: InlineRule[] = [
  { pattern: /`([^`\n]+)`/, build: (m) => ({ node: { kind: 'code', text: group(m, 1) } }) },
  {
    pattern: /\[([^\]\n]+)\]\(([^\s)]+)\)/,
    build: (m) =>
      SAFE_URL.test(group(m, 2))
        ? { node: { kind: 'link', href: group(m, 2), children: parseInline(group(m, 1)) } }
        : null,
  },
  {
    pattern: /https?:\/\/[^\s<>]+/,
    build: (m) => {
      // "(see https://example.com/x)." -- the closing bracket and full stop belong
      // to the sentence, so they are handed back to the text after the link.
      const href = trimAutolink(m[0])
      return SAFE_URL.test(href)
        ? {
            node: { kind: 'link', href, children: [{ kind: 'text', text: href }] },
            length: href.length,
          }
        : null
    },
  },
  {
    pattern: /\*\*(?!\s)([^*\n](?:[^\n]*?[^*\s\n])?)\*\*/,
    build: (m) => ({ node: { kind: 'strong', children: parseInline(group(m, 1)) } }),
  },
  // Emphasis must hug its content: "2 * 3 * 4" is arithmetic, not italics, and
  // "f_ck" is a symbol (CLAUDE.md 14.4), not the start of one.
  {
    pattern: /(?<![\w*])\*(?!\s)([^*\n]+?)(?<!\s)\*(?![\w*])/,
    build: (m) => ({ node: { kind: 'em', children: parseInline(group(m, 1)) } }),
  },
  {
    pattern: /(?<!\w)_(?!\s)([^_\n]+?)(?<!\s)_(?!\w)/,
    build: (m) => ({ node: { kind: 'em', children: parseInline(group(m, 1)) } }),
  },
]

export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  let rest = text

  while (rest.length > 0) {
    let best: { index: number; length: number; node: Inline } | null = null
    for (const rule of INLINE_RULES) {
      const match = rule.pattern.exec(rest)
      if (!match) continue
      if (best && match.index >= best.index) continue
      const built = rule.build(match)
      if (!built) continue
      best = { index: match.index, length: built.length ?? match[0].length, node: built.node }
    }

    if (!best) {
      pushText(out, rest)
      break
    }
    if (best.index > 0) pushText(out, rest.slice(0, best.index))
    out.push(best.node)
    rest = rest.slice(best.index + best.length)
  }
  return out
}

/** Text, with a newline inside a paragraph rendered as a line break. */
function pushText(out: Inline[], text: string): void {
  text.split('\n').forEach((line, index) => {
    if (index > 0) out.push({ kind: 'break' })
    if (line) out.push({ kind: 'text', text: line })
  })
}

const FENCE = /^```/
const HEADING = /^(#{1,3})\s+(.+?)\s*#*\s*$/
const QUOTE = /^>\s?/
const BULLET = /^\s*[-*+]\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/

export function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  const paragraph: string[] = []
  let i = 0

  const lineAt = (index: number): string => lines[index] ?? ''

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', children: parseInline(paragraph.join('\n')) })
    }
    paragraph.length = 0
  }

  while (i < lines.length) {
    const line = lineAt(i)

    if (FENCE.test(line)) {
      flushParagraph()
      const code: string[] = []
      i += 1
      while (i < lines.length && !FENCE.test(lineAt(i))) {
        code.push(lineAt(i))
        i += 1
      }
      i += 1 // closing fence, or end of input
      blocks.push({ kind: 'code', text: code.join('\n') })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      flushParagraph()
      const level = Math.min(3, Math.max(1, group(heading, 1).length)) as 1 | 2 | 3
      blocks.push({ kind: 'heading', level, children: parseInline(group(heading, 2)) })
      i += 1
      continue
    }

    if (QUOTE.test(line)) {
      flushParagraph()
      const quoted: string[] = []
      while (i < lines.length && QUOTE.test(lineAt(i))) {
        quoted.push(lineAt(i).replace(QUOTE, ''))
        i += 1
      }
      blocks.push({ kind: 'quote', children: parseInline(quoted.join('\n')) })
      continue
    }

    if (BULLET.test(line) || NUMBERED.test(line)) {
      flushParagraph()
      const ordered = NUMBERED.test(line)
      const marker = ordered ? NUMBERED : BULLET
      const items: Inline[][] = []
      while (i < lines.length) {
        const item = marker.exec(lineAt(i))
        if (!item) break
        items.push(parseInline(group(item, 1)))
        i += 1
      }
      blocks.push({ kind: 'list', ordered, items })
      continue
    }

    if (line.trim() === '') {
      flushParagraph()
      i += 1
      continue
    }

    paragraph.push(line)
    i += 1
  }
  flushParagraph()
  return blocks
}

function renderInline(nodes: Inline[]): ReactNode[] {
  return nodes.map((node, index) => {
    switch (node.kind) {
      case 'text':
        return <Fragment key={index}>{node.text}</Fragment>
      case 'break':
        return <br key={index} />
      case 'code':
        return (
          <code
            key={index}
            className="rounded bg-surface-raised px-1 py-0.5 font-mono text-[0.9em]"
          >
            {node.text}
          </code>
        )
      case 'strong':
        return <strong key={index}>{renderInline(node.children)}</strong>
      case 'em':
        return <em key={index}>{renderInline(node.children)}</em>
      case 'link':
        return (
          <a
            key={index}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="break-words text-accent underline underline-offset-2"
          >
            {renderInline(node.children)}
          </a>
        )
    }
  })
}

const HEADING_CLASS: Record<1 | 2 | 3, string> = {
  1: 'text-base font-semibold',
  2: 'text-sm font-semibold',
  3: 'text-sm font-medium',
}

/** Render markdown-ish text to React. Never HTML. */
export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks = parseBlocks(text)
  return (
    <div className={className ?? 'space-y-2 break-words text-sm leading-relaxed'}>
      {blocks.map((block, index) => {
        switch (block.kind) {
          case 'paragraph':
            return <p key={index}>{renderInline(block.children)}</p>
          case 'heading': {
            // Headings inside a ticket are visual structure, not document outline:
            // the page already has its h1/h2. Render them one level down.
            const Tag = block.level === 1 ? 'h4' : block.level === 2 ? 'h5' : 'h6'
            return (
              <Tag key={index} className={HEADING_CLASS[block.level]}>
                {renderInline(block.children)}
              </Tag>
            )
          }
          case 'code':
            return (
              <pre
                key={index}
                className="overflow-x-auto rounded-md border border-border bg-surface-raised p-3 font-mono text-xs leading-snug"
              >
                <code>{block.text}</code>
              </pre>
            )
          case 'quote':
            return (
              <blockquote key={index} className="border-s-2 border-border ps-3 text-text-muted">
                {renderInline(block.children)}
              </blockquote>
            )
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul'
            return (
              <Tag
                key={index}
                className={
                  block.ordered ? 'list-decimal space-y-0.5 ps-5' : 'list-disc space-y-0.5 ps-5'
                }
              >
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{renderInline(item)}</li>
                ))}
              </Tag>
            )
          }
        }
      })}
    </div>
  )
}
